import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import useBookmarks from "../lib/useBookmarks";
import useRecent from "../lib/useRecent";
import Concierge from "./Concierge";
import {
  addTerminalTools,
  createStack,
  createTerminal,
  fetchTools,
  getAuthUserSync,
  getMyTerminals,
  loadAuthUser,
  startGoogleLogin,
} from "../lib/api";
import { domainOf, slugify } from "../lib/utils-tv";
import { track } from "../lib/track";

/**
 * Brutalist Cmd+K launcher for TerminalValue.io.
 *
 * Doubles as a navigation surface: searches across tools AND function /
 * asset-class landing pages, with frecency-tracked "recently used" and a
 * persistent "★ pinned" section. Stays mounted globally; opens with ⌘/Ctrl+K.
 *
 *   ↵        open external (or jump to landing page for category/asset rows)
 *   ⌘↵       open internal detail page (tools only)
 *   ⌘S/Alt+S star/unstar
 *   Esc      close
 */
export default function CommandPalette() {
  const navigate = useNavigate();
  const { ids: pinnedIds, has: isStarred, toggle: toggleStar, replace } =
    useBookmarks();
  const recent = useRecent();
  const [open, setOpen] = useState(false);
  const [tools, setTools] = useState(null);
  const [stacks, setStacks] = useState(null); // user's saved terminals (null=unloaded)
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const [savingStack, setSavingStack] = useState(false);
  const [savedStack, setSavedStack] = useState(null);
  // Add-to-stack state: signed-in user, the tool currently being added via the
  // picker takeover, and a busy flag while a write is in flight.
  const [authUser, setAuthUser] = useState(getAuthUserSync() || null);
  const [pickerTool, setPickerTool] = useState(null);
  const [busyAdd, setBusyAdd] = useState(false);
  // Concierge "ask mode": when on, the palette becomes an AI chat (reuses the
  // shared <Concierge> in addMode so picks add straight to the active stack).
  const [askMode, setAskMode] = useState(false);
  const [askSeed, setAskSeed] = useState("");
  // Drill-down filters: when set, the palette filters to that category/asset
  // and the jump-to sections hide. Like a sub-mode of the launcher.
  const [drillFn, setDrillFn] = useState(null);
  const [drillAsset, setDrillAsset] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // ---- Global ⌘/Ctrl+K listener ----
  useEffect(() => {
    const onKey = (e) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
      if (isCmdK) {
        e.preventDefault();
        setOpen((v) => {
          if (!v) track("cmdk_open", { surface: "cmdk" });
          return !v;
        });
      } else if (e.key === "Escape" && open) {
        // First Esc clears any active drill filter / ask mode; next closes.
        if (askMode) {
          setAskMode(false);
          setAskSeed("");
        } else if (drillFn || drillAsset) {
          setDrillFn(null);
          setDrillAsset(null);
        } else {
          setOpen(false);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, drillFn, drillAsset, askMode]);

  // ---- Lazy-load tools on first open ----
  useEffect(() => {
    if (!open || tools !== null) return;
    fetchTools()
      .then((t) => setTools(t || []))
      .catch(() => {
        toast.error("could not load tools");
        setTools([]);
      });
  }, [open, tools]);

  // ---- Load the user's stacks on open (silent if signed out) ----
  useEffect(() => {
    if (!open || stacks !== null) return;
    getMyTerminals()
      .then((s) => setStacks(s || []))
      .catch(() => setStacks([]));
  }, [open, stacks]);

  useEffect(() => {
    setCursor(0);
  }, [query, open, drillFn, drillAsset]);

  useEffect(() => {
    if (!open) {
      setSavedStack(null);
      setQuery("");
      setDrillFn(null);
      setDrillAsset(null);
      setStacks(null); // re-fetch stacks next open so new ones appear
      setPickerTool(null);
      setBusyAdd(false);
      setAskMode(false);
      setAskSeed("");
    }
  }, [open]);

  // Resolve the signed-in user (controls whether add-to-stack writes or prompts login).
  useEffect(() => {
    if (!open) return;
    let alive = true;
    loadAuthUser()
      .then((u) => alive && setAuthUser(u))
      .catch(() => alive && setAuthUser(null));
    return () => {
      alive = false;
    };
  }, [open]);

  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  // ---- Build the unified result list from tools + derived categories ----
  // Each "row" has: type ('tool'|'function'|'asset'), key, payload.
  const sections = useMemo(() => {
    if (!tools) return null;

    // Count categories + assets from the live tool list (always accurate).
    const fnCounts = new Map();
    const assetCounts = new Map();
    for (const t of tools) {
      if (t.category) fnCounts.set(t.category, (fnCounts.get(t.category) || 0) + 1);
      if (t.category_2) fnCounts.set(t.category_2, (fnCounts.get(t.category_2) || 0) + 1);
      if (t.asset_class) assetCounts.set(t.asset_class, (assetCounts.get(t.asset_class) || 0) + 1);
    }
    const functions = [...fnCounts.entries()]
      .map(([name, count]) => ({ name, count, slug: slugify(name) }))
      .sort((a, b) => b.count - a.count);
    const assets = [...assetCounts.entries()]
      .map(([name, count]) => ({ name, count, slug: slugify(name) }))
      .sort((a, b) => b.count - a.count);

    const q = query.trim().toLowerCase();
    const matchTool = (t) => {
      if (drillFn && t.category !== drillFn && t.category_2 !== drillFn) return false;
      if (drillAsset && t.asset_class !== drillAsset) return false;
      return (
        !q ||
        t.title.toLowerCase().includes(q) ||
        (t.key_feature || "").toLowerCase().includes(q) ||
        (t.description || "").toLowerCase().includes(q) ||
        (t.url || "").toLowerCase().includes(q) ||
        (t.category || "").toLowerCase().includes(q) ||
        (t.asset_class || "").toLowerCase().includes(q)
      );
    };
    const matchCat = (c) => !q || c.name.toLowerCase().includes(q);

    const recentIds = recent.top(5).filter((id) => tools.some((t) => t.id === id));

    const pinned = pinnedIds
      .map((id) => tools.find((t) => t.id === id))
      .filter((t) => t && matchTool(t));
    const recentTools = recentIds
      .filter((id) => !pinnedIds.includes(id))
      .map((id) => tools.find((t) => t.id === id))
      .filter((t) => t && matchTool(t));
    // When drilled into a category/asset, hide the jump sections since we're
    // already inside one. User can adjust via the filter chips at the top.
    const drilled = !!(drillFn || drillAsset);
    const matchingFunctions = drilled ? [] : functions.filter(matchCat);
    const matchingAssets = drilled ? [] : assets.filter(matchCat);
    const matchingStacks = drilled
      ? []
      : (stacks || []).filter(
          (s) =>
            !q ||
            (s.title || "").toLowerCase().includes(q) ||
            (s.asset_class || "").toLowerCase().includes(q),
        );
    const restTools = tools.filter(
      (t) =>
        !pinnedIds.includes(t.id) &&
        !recentIds.includes(t.id) &&
        matchTool(t),
    );

    return {
      stacks: matchingStacks,
      pinned,
      recentTools,
      functions: matchingFunctions,
      assets: matchingAssets,
      restTools,
    };
  }, [tools, query, pinnedIds, recent, drillFn, drillAsset, stacks]);

  // Build flat list of rows with metadata for keyboard nav.
  const flat = useMemo(() => {
    if (!sections) return [];
    const rows = [];
    sections.stacks.forEach((s) =>
      rows.push({ type: "stack", id: `stk:${s.id}`, stack: s }),
    );
    sections.pinned.forEach((t) =>
      rows.push({ type: "tool", id: t.id, tool: t, kind: "pinned" }),
    );
    sections.recentTools.forEach((t) =>
      rows.push({ type: "tool", id: t.id, tool: t, kind: "recent" }),
    );
    sections.functions.forEach((c) =>
      rows.push({
        type: "function",
        id: `fn:${c.slug}`,
        cat: c,
      }),
    );
    sections.assets.forEach((c) =>
      rows.push({
        type: "asset",
        id: `as:${c.slug}`,
        cat: c,
      }),
    );
    sections.restTools.forEach((t) =>
      rows.push({ type: "tool", id: t.id, tool: t, kind: "all" }),
    );
    return rows;
  }, [sections]);

  const activeRow = flat[cursor] || null;

  // ---- Activate a row (Enter, or click) ----
  const activate = useCallback(
    (row, { cmd = false } = {}) => {
      if (!row) return;
      if (row.type === "stack") {
        const s = row.stack;
        if (cmd) {
          // ⌘↵ → open this stack in its own window (Bloomberg launchpad).
          window.open(`${window.location.origin}/?stack=${s.id}`, "_blank", "noopener");
          setOpen(false);
        } else {
          // ↵ → make it the active stack in THIS window (live, no reload).
          try {
            localStorage.setItem("tv_active_terminal_id", s.id);
          } catch (e) { /* ignore */ }
          window.dispatchEvent(new CustomEvent("tv:set-active-stack", { detail: s.id }));
          toast.success(`switched to ${s.title || "terminal"}`);
          setOpen(false);
        }
      } else if (row.type === "tool") {
        if (cmd) {
          if (row.tool.slug) {
            recent.bump(row.tool.id);
            setOpen(false);
            navigate(`/tool/${row.tool.slug}`);
          } else {
            toast.error("no detail page for this tool");
          }
        } else {
          recent.bump(row.tool.id);
          window.open(row.tool.url, "_blank", "noopener,noreferrer");
          setOpen(false);
        }
      } else if (row.type === "function") {
        if (cmd) {
          // ⌘↵ → navigate to the public landing page (leaves palette)
          setOpen(false);
          navigate(`/f/${row.cat.slug}`);
        } else {
          // ↵ → drill into this function inside the palette
          setDrillFn(row.cat.name);
          setQuery("");
        }
      } else if (row.type === "asset") {
        if (cmd) {
          setOpen(false);
          navigate(`/c/${row.cat.slug}`);
        } else {
          setDrillAsset(row.cat.name);
          setQuery("");
        }
      }
    },
    [navigate, recent],
  );

  // Enter the AI concierge "ask mode", optionally seeding the first question.
  const enterAsk = useCallback((seed) => {
    setAskSeed((seed || "").trim());
    setAskMode(true);
  }, []);

  const onInputKey = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setCursor((c) => Math.min(c + 1, Math.max(flat.length - 1, 0)));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setCursor((c) => Math.max(c - 1, 0));
      } else if (e.key === "Enter") {
        // `?<question>` → ask the concierge (power-user CLI gesture).
        const qt = query.trimStart();
        if (qt.startsWith("?")) {
          e.preventDefault();
          e.stopPropagation();
          enterAsk(qt.slice(1));
          return;
        }
        if (!activeRow) return;
        e.preventDefault();
        e.stopPropagation();
        activate(activeRow, { cmd: e.metaKey || e.ctrlKey });
      } else if (e.key.toLowerCase() === "s" && (e.metaKey || e.altKey)) {
        e.preventDefault();
        if (activeRow?.type === "tool") {
          toggleStar(activeRow.tool.id);
          toast.success(isStarred(activeRow.tool.id) ? "unstarred" : "starred");
        }
      }
    },
    [activeRow, flat.length, activate, toggleStar, isStarred, query, enterAsk],
  );

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-row="${cursor}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [cursor, open]);

  // ---- Save-and-share my stack ----
  const onShareStack = useCallback(async () => {
    if (pinnedIds.length === 0) {
      toast.error("star a tool first");
      return;
    }
    setSavingStack(true);
    try {
      const res = await createStack({ tool_ids: pinnedIds, title: null });
      const url = res.url || `${window.location.origin}/stack/${res.id}`;
      setSavedStack({ id: res.id, url });
    } catch {
      toast.error("could not save stack");
    } finally {
      setSavingStack(false);
    }
  }, [pinnedIds]);

  // ---- Add a tool to one of the user's stacks (terminals) ----
  const stackName = useCallback((s) => {
    if (!s) return "your terminal";
    const t = s.title || "terminal";
    if (s.asset_class && t.endsWith(` · ${s.asset_class}`)) {
      return t.slice(0, -(` · ${s.asset_class}`.length));
    }
    return t;
  }, []);

  const requireLogin = useCallback(() => {
    toast.error("log in to save to a stack", {
      action: {
        label: "log in",
        onClick: () =>
          startGoogleLogin(window.location.pathname + window.location.search),
      },
    });
  }, []);

  const doAdd = useCallback(
    async (stackId, tool) => {
      setBusyAdd(true);
      try {
        await addTerminalTools(stackId, [tool.slug]);
        track("terminal_add", { slug: tool.slug, surface: "cmdk" });
        const st = (stacks || []).find((s) => s.id === stackId);
        toast.success(`added ${tool.title} → ${stackName(st)}`);
        window.dispatchEvent(new CustomEvent("tv:stacks-changed"));
        getMyTerminals().then((s) => setStacks(s || [])).catch(() => {});
        setPickerTool(null);
      } catch {
        toast.error("could not add — are you the stack owner?");
      } finally {
        setBusyAdd(false);
      }
    },
    [stacks, stackName],
  );

  const addToNewStack = useCallback(async (tool) => {
    setBusyAdd(true);
    try {
      const res = await createTerminal({ slugs: [tool.slug] });
      track("terminal_create", { slug: tool.slug, surface: "cmdk" });
      const newId = res.primary_id || res.id;
      toast.success(`new stack created with ${tool.title}`);
      if (newId) {
        try {
          localStorage.setItem("tv_active_terminal_id", newId);
        } catch (e) {
          /* ignore */
        }
        window.dispatchEvent(new CustomEvent("tv:set-active-stack", { detail: newId }));
      }
      window.dispatchEvent(new CustomEvent("tv:stacks-changed"));
      getMyTerminals().then((s) => setStacks(s || [])).catch(() => {});
      setPickerTool(null);
    } catch {
      toast.error("could not create stack");
    } finally {
      setBusyAdd(false);
    }
  }, []);

  const resolveActiveStackId = useCallback(() => {
    let id = null;
    try {
      id = localStorage.getItem("tv_active_terminal_id");
    } catch (e) {
      /* ignore */
    }
    if (id && (stacks || []).some((s) => s.id === id)) return id;
    return stacks && stacks[0] ? stacks[0].id : null;
  }, [stacks]);

  const quickAdd = useCallback(
    (tool) => {
      if (!authUser) return requireLogin();
      if (!tool.slug) {
        toast.error("this tool can't be added to a stack");
        return;
      }
      const id = resolveActiveStackId();
      if (!id) {
        setPickerTool(tool); // no stacks yet → picker offers "new stack"
        return;
      }
      doAdd(id, tool);
    },
    [authUser, requireLogin, resolveActiveStackId, doAdd],
  );

  const openStackPicker = useCallback(
    (tool) => {
      if (!authUser) return requireLogin();
      if (!tool.slug) {
        toast.error("this tool can't be added to a stack");
        return;
      }
      setPickerTool(tool);
    },
    [authUser, requireLogin],
  );

  const submitNew = useCallback(() => {
    const q = query.trim();
    setOpen(false);
    navigate(q ? `/submit?title=${encodeURIComponent(q)}` : "/submit");
  }, [query, navigate]);

  // Concierge picks → add them all to the user's active stack (create one if
  // they have none), entirely inside the palette. Used as <Concierge addMode>'s
  // onAddTools so "ask → build" never leaves the CLI.
  const addRecommendedToActiveStack = useCallback(
    async (slugs) => {
      if (!authUser) return requireLogin();
      if (!slugs || !slugs.length) return;
      try {
        let id = resolveActiveStackId();
        if (!id) {
          const res = await createTerminal({ slugs });
          id = res.primary_id || res.id;
          if (id) {
            try {
              localStorage.setItem("tv_active_terminal_id", id);
            } catch (e) {
              /* ignore */
            }
            window.dispatchEvent(new CustomEvent("tv:set-active-stack", { detail: id }));
          }
          // The backend buckets slugs by asset class, so a cross-asset set can
          // create more than one stack — keep the toast honest.
          const nStacks = Array.isArray(res.terminals) ? res.terminals.length : 1;
          const nTools = `${slugs.length} tool${slugs.length === 1 ? "" : "s"}`;
          toast.success(
            nStacks > 1
              ? `built ${nStacks} stacks (${nTools}, split by asset class)`
              : `built a stack with ${nTools}`,
          );
        } else {
          await addTerminalTools(id, slugs);
          const st = (stacks || []).find((s) => s.id === id);
          toast.success(
            `added ${slugs.length} tool${slugs.length === 1 ? "" : "s"} → ${stackName(st)}`,
          );
        }
        window.dispatchEvent(new CustomEvent("tv:stacks-changed"));
        getMyTerminals().then((s) => setStacks(s || [])).catch(() => {});
      } catch {
        toast.error("could not add to stack");
      }
    },
    [authUser, requireLogin, resolveActiveStackId, stacks, stackName],
  );

  if (!open) return null;

  // Precompute row offsets so each section knows its starting flat index.
  const offsets = sections
    ? (() => {
        const stacks = 0;
        const pinned = sections.stacks.length;
        const recent = pinned + sections.pinned.length;
        const functions = recent + sections.recentTools.length;
        const assets = functions + sections.functions.length;
        const rest = assets + sections.assets.length;
        return { stacks, pinned, recent, functions, assets, rest };
      })()
    : null;

  const askPrefix = query.trimStart().startsWith("?");
  const askClean = query.replace(/^\s*\?\s*/, "").trim();

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/40 flex items-start justify-center pt-[10vh] px-4"
      onClick={() => setOpen(false)}
      data-testid="cmdk-overlay"
    >
      <div
        className="w-full max-w-2xl bg-white border-2 border-black font-mono shadow-[6px_6px_0_#000]"
        onClick={(e) => e.stopPropagation()}
        data-testid="cmdk-palette"
        role="dialog"
        aria-label="Command palette"
      >
        {askMode ? (
          <div data-testid="cmdk-ask">
            <div className="flex items-center border-b border-black bg-[#FBF8EE]">
              <span className="px-3 text-[14px] font-bold select-none">{">"}_</span>
              <span className="flex-1 py-2 text-[13px] text-black font-bold">
                ask the concierge
              </span>
              <button
                type="button"
                onClick={() => {
                  setAskMode(false);
                  setAskSeed("");
                }}
                className="px-3 text-[12px] text-[#888] hover:text-black"
                title="Back to search"
                data-testid="cmdk-ask-back"
              >
                ← back
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-3 text-[12px] text-[#888] hover:text-black border-l border-[#e6dfcc]"
                title="Close (Esc)"
                data-testid="cmdk-ask-close"
              >
                esc
              </button>
            </div>
            <div className="max-h-[68vh] overflow-y-auto p-3" data-testid="cmdk-ask-body">
              <Concierge
                addMode
                autoRun
                seedQuery={askSeed}
                onAddTools={addRecommendedToActiveStack}
                surface="cmdk"
              />
            </div>
          </div>
        ) : (
          <>
        {/* Input */}
        <div className="flex items-center border-b border-black">
          <span className="px-3 text-black text-base font-bold select-none">{">"}</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="search tools · ↵ open · ? ask the concierge · ⌘S star"
            className="flex-1 bg-transparent outline-none py-2 text-[14px] text-black placeholder:text-[#888]"
            data-testid="cmdk-input"
            spellCheck={false}
            autoComplete="off"
          />
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="px-3 text-[12px] text-[#888] hover:text-black"
            title="Close (Esc)"
            data-testid="cmdk-close"
          >
            esc
          </button>
        </div>

        {/* Drill-down filter chips — only shown when a category/asset is selected. */}
        {(drillFn || drillAsset) && !savedStack && (
          <div
            className="flex flex-wrap items-center gap-2 px-3 py-1.5 border-b border-[#eee] bg-[#fafafa] text-[12px]"
            data-testid="cmdk-filter-chips"
          >
            <span className="text-[#888]">filtered:</span>
            {drillFn && (
              <button
                type="button"
                onClick={() => setDrillFn(null)}
                className="px-1.5 py-0.5 border border-black bg-white text-[11px] hover:bg-black hover:text-white"
                data-testid="cmdk-chip-fn"
                title="Remove function filter (Esc)"
              >
                function: {drillFn} ✕
              </button>
            )}
            {drillAsset && (
              <button
                type="button"
                onClick={() => setDrillAsset(null)}
                className="px-1.5 py-0.5 border border-black bg-white text-[11px] hover:bg-black hover:text-white"
                data-testid="cmdk-chip-asset"
                title="Remove asset filter (Esc)"
              >
                asset: {drillAsset} ✕
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (drillFn) navigate(`/f/${slugify(drillFn)}`);
                else if (drillAsset) navigate(`/c/${slugify(drillAsset)}`);
                setOpen(false);
              }}
              className="ml-auto tv-link text-[11px]"
              data-testid="cmdk-chip-open-page"
              title="Open the public landing page for this filter (⌘↵ on the row also does this)"
            >
              [↗ open full page]
            </button>
          </div>
        )}

        {/* Share-panel takes over when a stack was just saved. */}
        {savedStack ? (
          <SharePanel
            saved={savedStack}
            count={pinnedIds.length}
            onClose={() => setSavedStack(null)}
            onOpen={() => {
              setOpen(false);
              navigate(`/stack/${savedStack.id}`);
            }}
          />
        ) : null}

        {/* Add-to-stack picker takes over (like the share panel) when expanding. */}
        {pickerTool && !savedStack ? (
          <AddToStackPanel
            tool={pickerTool}
            stacks={stacks}
            busy={busyAdd}
            nameOf={stackName}
            onAdd={(sid) => doAdd(sid, pickerTool)}
            onNew={() => addToNewStack(pickerTool)}
            onClose={() => setPickerTool(null)}
          />
        ) : null}

        <div
          ref={listRef}
          className="max-h-[60vh] overflow-y-auto text-[13px]"
          data-testid="cmdk-results"
          hidden={!!savedStack || !!pickerTool}
        >
          {tools === null && (
            <div className="px-3 py-4 text-[#888]" data-testid="cmdk-loading">
              loading...<span className="tv-blink">_</span>
            </div>
          )}

          {sections && flat.length === 0 && (
            <div className="px-3 py-4" data-testid="cmdk-empty">
              {askPrefix ? (
                <div className="text-[#888]">
                  press <Kbd>↵</Kbd> to ask the concierge
                  {askClean ? <> about &quot;{askClean}&quot;</> : ""}
                </div>
              ) : (
                <>
                  <div className="text-[#888]">
                    no matches{query.trim() ? ` for "${query.trim()}"` : ""}
                  </div>
                  {query.trim() && (
                    <button
                      type="button"
                      onClick={submitNew}
                      className="mt-2 tv-btn"
                      data-testid="cmdk-submit-new"
                    >
                      [＋ add &quot;{query.trim()}&quot; to the index]
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {sections && sections.stacks.length > 0 && (
            <Section label={`my stacks (${sections.stacks.length}) · ↵ switch · ⌘↵ new window`}>
              {sections.stacks.map((s, i) => (
                <StackRow
                  key={s.id}
                  stack={s}
                  active={cursor === offsets.stacks + i}
                  index={offsets.stacks + i}
                  onActivate={(cmd) => activate(flat[offsets.stacks + i], { cmd })}
                />
              ))}
            </Section>
          )}

          {sections && sections.pinned.length > 0 && (
            <Section label={`★ pinned (${sections.pinned.length})`}>
              {sections.pinned.map((t, i) => (
                <ToolRow
                  key={t.id}
                  tool={t}
                  active={cursor === offsets.pinned + i}
                  starred
                  index={offsets.pinned + i}
                  onActivate={(cmd) =>
                    activate(flat[offsets.pinned + i], { cmd })
                  }
                  onQuickAdd={() => quickAdd(t)}
                  onPickStack={() => openStackPicker(t)}
                  onStar={() => toggleStar(t.id)}
                />
              ))}
            </Section>
          )}

          {sections && sections.recentTools.length > 0 && (
            <Section label={`recently used (${sections.recentTools.length})`}>
              {sections.recentTools.map((t, i) => (
                <ToolRow
                  key={t.id}
                  tool={t}
                  active={cursor === offsets.recent + i}
                  starred={isStarred(t.id)}
                  index={offsets.recent + i}
                  onActivate={(cmd) =>
                    activate(flat[offsets.recent + i], { cmd })
                  }
                  onQuickAdd={() => quickAdd(t)}
                  onPickStack={() => openStackPicker(t)}
                  onStar={() => toggleStar(t.id)}
                />
              ))}
            </Section>
          )}

          {sections && sections.functions.length > 0 && (
            <Section label={`jump to function (${sections.functions.length})`}>
              {sections.functions.map((c, i) => (
                <CategoryRow
                  key={c.slug}
                  cat={c}
                  symbol="↪"
                  prefix="/f/"
                  active={cursor === offsets.functions + i}
                  index={offsets.functions + i}
                  onActivate={() => activate(flat[offsets.functions + i])}
                />
              ))}
            </Section>
          )}

          {sections && sections.assets.length > 0 && (
            <Section label={`jump to asset (${sections.assets.length})`}>
              {sections.assets.map((c, i) => (
                <CategoryRow
                  key={c.slug}
                  cat={c}
                  symbol="↪"
                  prefix="/c/"
                  active={cursor === offsets.assets + i}
                  index={offsets.assets + i}
                  onActivate={() => activate(flat[offsets.assets + i])}
                />
              ))}
            </Section>
          )}

          {sections && sections.restTools.length > 0 && (
            <Section label={`all tools (${sections.restTools.length})`}>
              {sections.restTools.map((t, i) => (
                <ToolRow
                  key={t.id}
                  tool={t}
                  active={cursor === offsets.rest + i}
                  starred={isStarred(t.id)}
                  index={offsets.rest + i}
                  onActivate={(cmd) =>
                    activate(flat[offsets.rest + i], { cmd })
                  }
                  onQuickAdd={() => quickAdd(t)}
                  onPickStack={() => openStackPicker(t)}
                  onStar={() => toggleStar(t.id)}
                />
              ))}
            </Section>
          )}

          {sections && query.trim() && flat.length > 0 && (
            <button
              type="button"
              onClick={submitNew}
              className="w-full text-left px-3 py-2 text-[12px] text-[#888] hover:text-black hover:bg-[#fafafa] border-t border-[#eee]"
              data-testid="cmdk-submit-hint"
            >
              ＋ can&apos;t find it? add &quot;{query.trim()}&quot; to the index
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-black px-3 py-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[11px] text-[#666] bg-[#fafafa]">
          <Kbd>⌘K</Kbd> open · <Kbd>↑↓</Kbd> nav · <Kbd>↵</Kbd>{" "}
          {drillFn || drillAsset ? "open tool" : "open / drill"} ·{" "}
          <Kbd>⌘↵</Kbd> {drillFn || drillAsset ? "details" : "page / details"} ·{" "}
          <Kbd>⌘S</Kbd> star · <Kbd>?</Kbd> ask AI
          {(drillFn || drillAsset) && (
            <span className="text-[#888]">
              · <Kbd>Esc</Kbd> clear filter
            </span>
          )}
          <span className="ml-auto flex items-baseline gap-3">
            {pinnedIds.length > 0 && (
              <>
                <span className="text-[#888]">{pinnedIds.length} starred</span>
                <button
                  type="button"
                  onClick={onShareStack}
                  disabled={savingStack}
                  className="tv-link disabled:opacity-50"
                  data-testid="cmdk-share-stack"
                  title="Save your starred tools as a shareable stack URL"
                >
                  {savingStack ? "saving..." : "[save & share]"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    replace([]);
                    toast.success("cleared");
                  }}
                  className="tv-link text-[#888]"
                  data-testid="cmdk-clear-stars"
                  title="Unstar all"
                >
                  clear
                </button>
              </>
            )}
          </span>
        </div>
          </>
        )}
      </div>
    </div>
  );
}

function StackRow({ stack, active, index, onActivate }) {
  const title =
    stack.asset_class && stack.title && stack.title.endsWith(` · ${stack.asset_class}`)
      ? stack.title.slice(0, -(` · ${stack.asset_class}`.length))
      : stack.title;
  return (
    <div
      data-row={index}
      data-testid={`cmdk-stack-${stack.id}`}
      onClick={() => onActivate(false)}
      className={`flex items-baseline gap-3 px-3 py-1.5 cursor-pointer border-b border-[#f0f0f0] ${
        active ? "bg-[#fff5dd]" : "hover:bg-[#fafafa]"
      }`}
    >
      <span className="text-[14px] leading-none text-[#bbb]">▦</span>
      <span className="text-[#888] tabular-nums text-[11px] min-w-[16px]">
        {active ? "▸" : " "}
      </span>
      <span className="font-bold text-black truncate flex-1 min-w-0">
        {">"}_ {title || "untitled terminal"}
      </span>
      {stack.asset_class && (
        <span className="text-[11px] text-[#888] truncate hidden sm:inline max-w-[140px]">
          {stack.asset_class}
        </span>
      )}
      <span className="text-[11px] text-[#888] tabular-nums">
        {stack.count != null ? stack.count : "?"} tools
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onActivate(true);
        }}
        className="text-[11px] text-[#888] hover:text-black hover:underline"
        title="Open this stack in its own window (⌘↵)"
        data-testid={`cmdk-stack-newwindow-${stack.id}`}
      >
        ⌘↵
      </button>
    </div>
  );
}

function Kbd({ children }) {
  return (
    <span className="px-1 py-0.5 border border-[#bbb] bg-white text-[10px] text-[#444] font-mono">
      {children}
    </span>
  );
}

function Section({ label, children }) {
  return (
    <>
      <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-[#888] bg-[#fafafa] border-b border-[#eee] sticky top-0">
        {label}
      </div>
      {children}
    </>
  );
}

function ToolRow({ tool, active, starred, index, onActivate, onStar, onQuickAdd, onPickStack }) {
  return (
    <div
      data-row={index}
      data-testid={`cmdk-row-${tool.id}`}
      onClick={() => onActivate(false)}
      className={`flex items-baseline gap-3 px-3 py-1.5 cursor-pointer border-b border-[#f0f0f0] ${
        active ? "bg-[#fff5dd]" : "hover:bg-[#fafafa]"
      }`}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onStar();
        }}
        className={`text-[14px] leading-none ${
          starred ? "text-black" : "text-[#bbb] hover:text-black"
        }`}
        title={starred ? "Unstar" : "Star"}
        data-testid={`cmdk-star-${tool.id}`}
      >
        ★
      </button>
      <span className="text-[#888] tabular-nums text-[11px] min-w-[16px]">
        {active ? "▸" : " "}
      </span>
      <span className="font-bold text-black truncate flex-1 min-w-0">
        {tool.title}
      </span>
      <span className="text-[11px] text-[#888] truncate hidden sm:inline max-w-[180px]">
        {domainOf(tool.url)}
      </span>
      <span className="text-[11px] text-[#888] truncate hidden md:inline max-w-[120px]">
        {tool.category}
      </span>
      <span className="text-[11px] text-[#000] tabular-nums">
        {tool.pricing}
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onQuickAdd && onQuickAdd();
        }}
        className="text-[11px] text-[#888] hover:text-black hover:underline whitespace-nowrap"
        title="Add to your active stack"
        data-testid={`cmdk-addstack-${tool.id}`}
      >
        +stk
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onPickStack && onPickStack();
        }}
        className="text-[11px] text-[#888] hover:text-black"
        title="Add to another stack…"
        data-testid={`cmdk-pickstack-${tool.id}`}
      >
        ▾
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onActivate(true);
        }}
        className="text-[11px] text-[#888] hover:text-black hover:underline"
        title="Open detail page (⌘↵)"
        data-testid={`cmdk-details-${tool.id}`}
      >
        ⌘↵
      </button>
    </div>
  );
}

function CategoryRow({ cat, symbol, prefix, active, index, onActivate }) {
  return (
    <div
      data-row={index}
      data-testid={`cmdk-jump-${prefix.replace(/\//g, "")}${cat.slug}`}
      onClick={onActivate}
      className={`flex items-baseline gap-3 px-3 py-1.5 cursor-pointer border-b border-[#f0f0f0] ${
        active ? "bg-[#fff5dd]" : "hover:bg-[#fafafa]"
      }`}
    >
      <span className="text-[14px] leading-none text-[#bbb]">{symbol}</span>
      <span className="text-[#888] tabular-nums text-[11px] min-w-[16px]">
        {active ? "▸" : " "}
      </span>
      <span className="text-black truncate flex-1 min-w-0">
        {cat.name}
      </span>
      <span className="text-[11px] text-[#888] tabular-nums">
        {cat.count} {cat.count === 1 ? "tool" : "tools"}
      </span>
      <span className="text-[11px] text-[#888] truncate hidden sm:inline max-w-[200px]">
        {prefix}
        {cat.slug}
      </span>
    </div>
  );
}

function AddToStackPanel({ tool, stacks, busy, nameOf, onAdd, onNew, onClose }) {
  const list = stacks || [];
  return (
    <div className="p-4 border-b border-black" data-testid="cmdk-addstack-panel">
      <div className="text-[12px] text-[#888] uppercase tracking-wider mb-2">
        add <span className="text-black font-bold normal-case">{tool.title}</span> to…
      </div>
      <div className="max-h-[40vh] overflow-y-auto">
        {list.length === 0 && (
          <div className="text-[12px] text-[#888] mb-2" data-testid="cmdk-addstack-none">
            you have no stacks yet — create one below.
          </div>
        )}
        <ul className="list-none p-0 m-0 space-y-1">
          {list.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                disabled={busy}
                onClick={() => onAdd(s.id)}
                className="w-full text-left px-2 py-1.5 border border-[#ddd] hover:border-black hover:bg-[#fafafa] disabled:opacity-50 flex items-baseline gap-2"
                data-testid={`cmdk-addstack-to-${s.id}`}
              >
                <span className="font-bold text-black truncate flex-1 min-w-0">
                  {">"}_ {nameOf(s)}
                </span>
                {s.asset_class && (
                  <span className="text-[11px] text-[#888] truncate hidden sm:inline max-w-[120px]">
                    {s.asset_class}
                  </span>
                )}
                <span className="text-[11px] text-[#888] tabular-nums">
                  {s.count != null ? s.count : "?"} tools
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={onNew}
          className="tv-btn disabled:opacity-50"
          data-testid="cmdk-addstack-new"
        >
          [＋ new stack]
        </button>
        <button
          type="button"
          onClick={onClose}
          className="tv-link text-[#888] ml-auto"
          data-testid="cmdk-addstack-back"
        >
          ← back
        </button>
      </div>
    </div>
  );
}

function SharePanel({ saved, count, onClose, onOpen }) {
  return (
    <div className="p-4 border-b border-black" data-testid="cmdk-share-panel">
      <div className="text-[12px] text-[#888] uppercase tracking-wider mb-1">
        ★ stack saved · {count} {count === 1 ? "tool" : "tools"}
      </div>
      <div
        className="border border-[#ddd] bg-[#fafafa] px-3 py-2 text-[13px] break-all select-all"
        data-testid="cmdk-share-url"
      >
        {saved.url}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-[12px]">
        <button
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(saved.url);
              toast.success("link copied");
            } catch {
              toast.error("could not copy — select and copy manually");
            }
          }}
          className="tv-btn"
          data-testid="cmdk-share-copy"
        >
          [copy link]
        </button>
        <button
          type="button"
          onClick={onOpen}
          className="tv-btn"
          data-testid="cmdk-share-open"
        >
          [↗ open stack page]
        </button>
        <span className="text-[#888] ml-2">share:</span>
        <a
          href={`https://x.com/intent/post?text=${encodeURIComponent(
            `my investing-tools stack — ${count} tools`,
          )}&url=${encodeURIComponent(saved.url)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="tv-link"
          data-testid="cmdk-share-twitter"
        >
          x
        </a>
        <span className="text-[#bbb]">·</span>
        <a
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
            saved.url,
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="tv-link"
          data-testid="cmdk-share-linkedin"
        >
          linkedin
        </a>
        <span className="text-[#bbb]">·</span>
        <a
          href={`mailto:?subject=${encodeURIComponent(
            "my investing-tools stack",
          )}&body=${encodeURIComponent(saved.url)}`}
          className="tv-link"
          data-testid="cmdk-share-email"
        >
          email
        </a>
        <button
          type="button"
          onClick={onClose}
          className="tv-link text-[#888] ml-auto"
          data-testid="cmdk-share-back"
          title="Back to search"
        >
          ← back
        </button>
      </div>
    </div>
  );
}
